<template>
  <div v-if="order && !order.is_remote">
    <div class="d-flex align-items-start mt-3">
      <JobTypes :job="order" class="mr-2" />
      <h2 class="mb-0">
        <template v-if="jobTypeLabel(order)">
          {{ jobTypeLabel(order) }} {{ $t('at') }}
        </template>
        {{ order.customer && order.customer.name }}
        <template v-if="jobCustomerCity(order)">
          | {{ jobCustomerCity(order) }}
        </template>
        — {{ order.job.name }}
      </h2>
    </div>
    <div class="d-flex align-items-center mt-2 mb-4 text-muted">
      <img
        v-if="jobBrandLogo(order)"
        :src="jobBrandLogo(order)"
        :alt="order.brand.name"
        class="brand-logo mr-2"
      />
      <small>{{ brandDisplayName }}</small>
    </div>
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('address') }}:</b-col>
      <b-col cols="12" lg="9">
        <a
          :href="`https://maps.google.com/?q=${order.customer.shipping_address.street}+${order.customer.shipping_address.city}+${order.customer.shipping_address.postal_code}`"
          target="_blank"
        >
          {{ order.customer.name }},
          <br />
          {{ order.customer.shipping_address.street }},
          <br />
          <template v-if="order.customer.company_name">
            {{ order.customer.company_name }},
            <br />
          </template>
          {{ order.customer.shipping_address.postal_code }}
          {{ order.customer.shipping_address.city }}
        </a>
        <div v-if="order.job_contact_person.first_name" class="mb-0">
          <p class="mb-0">
            <strong>{{ $t('contact-person.title') }}:</strong>
          </p>
          <p class="my-0">
            {{
              `${order.job_contact_person.first_name} ${order.job_contact_person.last_name}`
            }}
          </p>
          <br />
        </div>
      </b-col>
    </b-row>
    <template v-if="isCustomerSignatureMissing">
      <b-row class="border-bottom py-3">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('inputs.headings.customer-signature-timestamp') }}:
        </b-col>
        <b-col cols="12" lg="3">
          <p class="m-0">{{ new Date() | moment('DD MMM YYYY, HH:mm') }}</p>
        </b-col>
      </b-row>
      <input-holder
        v-if="Object.keys(orderIntakeInputExtra).length > 0"
        input-name="order-intake-protocol"
        type="order-intake-forms"
      >
        <order-intake-forms
          type="order-intake-forms"
          input-name="order-intake-protocol"
          :elementId="this.order.job.sfid"
          :opportunitySfid="this.order.opportunity.sfid"
          :orderApiId="this.order.api_id"
          :accessToken="this.access_token"
          @redirect="orderIntakeFormsRedirect"
          v-bind.sync="orderIntakeInputExtra"
        />
      </input-holder>
    </template>
    <b-icon
      v-else
      icon="check2-circle"
      variant="primary"
      class="success-icon"
    ></b-icon>
  </div>
  <not-found v-else-if="order === false" />
</template>

<script>
/* eslint-disable vue/no-unused-components */
// eslint can't detect the dynamic usage of the components

import axios from 'axios';
import { mapGetters } from 'vuex';
import OrderIntakeForms from '@gid/vue-common/components/OrderIntakeForms';
import {
  CustomerSignatureStatusEnum,
  FormOrderModificationCategoryEnum,
} from '@gid/models';
import { JobTypeEnum } from '@gid/models/dist/entities/job-type-enum';
import NotFound from '@/views/NotFound.vue';
import InputHolder from './InputHolder.vue';
import { BIcon, BIconCheck2Circle } from 'bootstrap-vue';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';
import jobDisplay from '@/mixins/jobDisplayMixin';

export default {
  mixins: [jobDisplay],
  components: {
    NotFound,
    InputHolder,
    OrderIntakeForms,
    BIcon,
    BIconCheck2Circle,
    JobTypes,
  },
  props: {
    job_id: {
      type: String,
      required: true,
    },
  },
  watch: {
    job_id() {
      this.loadData();
    },
    order(newValue, oldValue) {
      if (newValue) {
        this.loadOrderIntakeExtra();
      }
    },
  },
  data() {
    return {
      order: null,
      orderIntakeInputExtra: {},
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['locale', 'access_token']),
    brandDisplayName() {
      return this.order?.opportunity?.name || '';
    },
    isCustomerSignatureMissing() {
      // Include PENDING status to support started.customer-signature.pending state
      return [
        CustomerSignatureStatusEnum.MISSING,
        CustomerSignatureStatusEnum.PENDING,
      ].includes(this.order.job.customer_signature);
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    resolveOifLocale(fallbackLocale) {
      const localeFromQuery = this.$route?.query?.locale;
      const localeCandidate =
        typeof localeFromQuery === 'string' && localeFromQuery
          ? localeFromQuery
          : this.locale || fallbackLocale;
      const normalizedLocale = String(localeCandidate || '')
        .toLowerCase()
        .split('-')[0];
      return ['de', 'en', 'fr'].includes(normalizedLocale)
        ? normalizedLocale
        : fallbackLocale;
    },
    redirect(job = null) {
      // Use provided job data or fall back to this.order.job
      const jobData = job || this.order.job;

      // Check if both protocol is submitted AND customer signature is collected
      // In this case, the jobs-service will transition the job to APPROVAL_PENDING
      // so we should redirect to 'order-complete' instead of the default redirect
      const signatureCollected =
        jobData.customer_signature === CustomerSignatureStatusEnum.TAKEN ||
        (jobData.customer_signature === CustomerSignatureStatusEnum.DECLINED &&
          jobData.customer_signature_reason !== null);

      if (jobData.protocol_submitted && signatureCollected) {
        this.$router.push({
          name: 'order-complete',
          params: { job_id: this.order.sfid },
        });
        return;
      }

      const redirect = this.$router.currentRoute?.query?.redirect;
      const name = this.$router.currentRoute?.query?.name;
      const type = this.$router.currentRoute?.query?.type;
      if (redirect && name && type) {
        this.$router.push({
          name,
          params: { type, job_id: this.order.sfid },
        });
      } else {
        this.$router.push({
          name: 'orders-customer-signature',
        });
      }
    },
    loadData() {
      this.order = null;
      axios
        .get(`/api/partner/orders/${this.job_id}`)
        .then((response) => {
          this.order = response.data;
          this.$store.commit(
            'title',
            this.$t('order-with-number', { num: this.order.job.name }),
          );
        })
        .catch((error) => {
          if (error.response && error.response.status == 404) {
            this.order = false;
          }
        });
    },
    async loadOrderIntakeExtra() {
      if (this.order) {
        const formType = this.getFormTypeFromJobType();
        if (!formType) {
          return;
        }
        const extra = {};
        const customerFormUrl = `/order-intake-forms-api/form/${this.order.opportunity.sfid}/form-type/${formType}/protocol-type/default/category/${FormOrderModificationCategoryEnum.CONFIRMATION}`;
        let customerForm = null;
        try {
          const customerFormResponse = await axios.get(customerFormUrl);
          customerForm = customerFormResponse.data;
          if (customerForm) {
            extra.locale = this.resolveOifLocale(customerForm.locale);
            extra.form = customerForm.id;
            extra.theme = customerForm.theme;
          }
        } catch (err) {
          console.error(err);
        }
        this.orderIntakeInputExtra = extra;
      }
    },
    getFormTypeFromJobType() {
      switch (true) {
        case this.order.is_precheck:
          return JobTypeEnum.PRECHECK;
        case this.order.is_installation:
          return JobTypeEnum.INSTALLATION;
        case this.order.is_meter_application:
          return JobTypeEnum.METER_APPLICATION;
        case this.order.is_installation_preparation:
          return JobTypeEnum.INSTALLATION_PREPARATION;
        case this.order.is_maintenance:
          return JobTypeEnum.MAINTENANCE;
        case this.order.is_defect_elimination:
          return JobTypeEnum.DEFECT_ELIMINATION;
        case this.order.is_hardware_sale:
          return JobTypeEnum.HARDWARE_SALE;
        case this.order.is_customer_sign_required:
          return 'None';
        default:
          return null;
      }
    },
    orderIntakeFormsRedirect(data) {
      // The form submission response (data.job) has the UPDATED customer_signature,
      // but may not include protocol_submitted if the redirect payload doesn't have it.
      // The local order data has protocol_submitted from when page loaded.
      // Merge them with form data taking priority for fields it provides.
      const mergedJobData = {
        protocol_submitted: this.order.job.protocol_submitted,
        customer_signature: this.order.job.customer_signature,
        customer_signature_reason: this.order.job.customer_signature_reason,
        ...data.job, // Form submission data overrides local data
      };
      this.redirect(mergedJobData);
    },
  },
};
</script>

<style lang="scss">
.b-icon.bi.success-icon {
  font-size: 16rem;
  padding: 2rem;
  display: block;
  margin: 0 auto;
}

.action {
  display: flex;
}
</style>

<style scoped>
.brand-logo {
  max-height: 24px;
  max-width: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
